import React, { useState, useEffect, useCallback } from 'react';
import ChatCard from '../ChatCard';
import { useTruncatedText } from '../useTruncatedText';
import '../ChatCard.css';
import './EventCards.css';

const COLLAPSED_LINES = 3;

/**
 * Render description text with minimal **bold** support.
 * Splits on `**...**` and wraps matches in <strong>; everything else stays plain text.
 */
const renderDescription = (text) => {
  if (!text) return null;
  const segments = text.split(/(\*\*[^*]+\*\*)/g);
  return segments.map((segment, idx) => {
    if (segment.startsWith('**') && segment.endsWith('**')) {
      return <strong key={idx}>{segment.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={idx}>{segment}</React.Fragment>;
  });
};

/**
 * Parse a backend-formatted datetime like "May 13, 2026 09:30 AM MDT"
 * into structured parts. Returns null if the format does not match.
 */
const parseFormattedDateTime = (str) => {
  if (!str) return null;
  const match = str.match(/^(\w+)\s+(\d+),\s*(\d{4})\s+(\d{1,2}):(\d{2})\s+([AP]M)\s+(\S+)$/);
  if (!match) return null;
  const hour = parseInt(match[4], 10); // drops leading zero
  return {
    month: match[1],
    day: parseInt(match[2], 10),
    year: parseInt(match[3], 10),
    time: `${hour}:${match[5]} ${match[6]}`,
    tz: match[7],
  };
};

/**
 * Render a compact datetime string. Collapses redundant date parts when
 * start and end overlap on year / month / day:
 *   Same day:        "May 13, 2026 · 9:30 AM – 6:30 PM MDT"
 *   Same month:      "May 13 – 19, 2026 · 9:30 AM – 6:30 PM MDT"
 *   Same year:       "May 13 – Jun 03, 2026 · 9:30 AM – 6:30 PM MDT"
 *   Different years: "May 13, 2026 9:30 AM – Jun 03, 2027 6:30 PM MDT"
 * If the strings can't be parsed, the original backend text is preserved.
 */
const formatEventDateTime = (formattedStart, formattedEnd) => {
  if (!formattedStart) return '';
  if (!formattedEnd) return formattedStart;

  const start = parseFormattedDateTime(formattedStart);
  const end = parseFormattedDateTime(formattedEnd);
  if (!start || !end) {
    return `${formattedStart} — ${formattedEnd}`;
  }

  const sameYear = start.year === end.year;
  const sameMonth = sameYear && start.month === end.month;
  const sameDay = sameMonth && start.day === end.day;
  const times = `${start.time} – ${end.time} ${end.tz}`;

  if (sameDay) {
    return `${start.month} ${start.day}, ${start.year} · ${times}`;
  }
  if (sameMonth) {
    return `${start.month} ${start.day} – ${end.day}, ${end.year} · ${times}`;
  }
  if (sameYear) {
    return `${start.month} ${start.day} – ${end.month} ${end.day}, ${end.year} · ${times}`;
  }
  return `${start.month} ${start.day}, ${start.year} ${start.time} – ${end.month} ${end.day}, ${end.year} ${end.time} ${end.tz}`;
};

/**
 * One event card. Owns its own description ref so the parent `events.map()`
 * can host independent per-card measurement.
 */
const EventCardItem = ({ event, isExpanded, onToggle, onImageLoad }) => {
  const { ref: descRef, truncatedText, isLong: hasLongDescription } =
    useTruncatedText(event.description, COLLAPSED_LINES);

  const visibleText = isExpanded ? event.description : truncatedText;

  return (
    <ChatCard
      image={event.image}
      imageAlt={event.title}
      onImageLoad={onImageLoad}
    >
      <h4 className="event-card-title">
        {event.title}
        {event.is_ongoing && <span className="event-ongoing-badge">ONGOING</span>}
      </h4>
      <p className="event-card-datetime">
        <span className="event-card-icon" aria-hidden="true">📅</span>
        <span className="event-card-row-text">
          {formatEventDateTime(event.formatted_start, event.formatted_end)}
        </span>
      </p>
      {event.location_name && (
        <p className="event-card-location">
          <span className="event-card-icon" aria-hidden="true">📍</span>
          <span className="event-card-row-text">{event.location_name}</span>
        </p>
      )}
      {event.where && (
        <p className="event-card-venue">
          <span className="event-card-icon" aria-hidden="true">📌</span>
          <span className="event-card-row-text">{event.where}</span>
        </p>
      )}
      {event.description && (
        <p
          ref={descRef}
          className={`event-card-description${isExpanded ? ' is-expanded' : ''}`}
        >
          {renderDescription(visibleText)}
          {hasLongDescription && (
            <>
              {' '}
              <button
                type="button"
                className="event-card-toggle"
                onClick={onToggle}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? 'Hide event details' : 'Show full event details'}
              >
                {isExpanded ? 'Hide details' : 'Show more'}
              </button>
            </>
          )}
        </p>
      )}
    </ChatCard>
  );
};

/**
 * Display organization event cards with expandable details.
 *
 * Each card is independent — toggling one does not affect others.
 * State is component-local, so a page reload always starts collapsed.
 *
 * @param {Array} events - Event objects from raw_output.events
 * @param {function} [onImageLoad] - Callback when an image loads (e.g. scrollToBottom)
 * @param {*} [collapseSignal] - When this prop changes, all expanded cards collapse
 *                                (used to reset state when user sends a new message).
 */
const EventCards = ({ events, onImageLoad, collapseSignal }) => {
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    setExpandedIds(new Set());
  }, [collapseSignal]);

  const toggleExpanded = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (!events || events.length === 0) return null;

  return (
    <div className="chat-cards-grid event-cards-grid">
      {events.map(event => (
        <EventCardItem
          key={event.id}
          event={event}
          isExpanded={expandedIds.has(event.id)}
          onToggle={() => toggleExpanded(event.id)}
          onImageLoad={onImageLoad}
        />
      ))}
    </div>
  );
};

export default React.memo(EventCards);
